import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { BassitLogo } from '@/components/BassitLogo';
import { BassitWordmark } from '@/components/BassitWordmark';
import { LangToggle } from '@/components/LangToggle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Arrow, Spinner } from '@/components/icons';
import { useThemeAndLocale } from '@/hooks/useThemeAndLocale';
import { LOGIN_I18N } from '@/i18n/loginStrings';

/* ───────────────────────────── auth helpers (preserved) ─────────────────────────── */

type DiscoverTenant = {
  tenantId: string;
  tenantName?: string;
  tenantHost?: string;
  role?: string;
  redirectUrl: string;
};

const REQUEST_TIMEOUT_MS = 30_000;

const normalizeUrl = (value?: string): string =>
  String(value || '').trim().replace(/\/+$/, '');

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const runtime = (window as any).__RUNTIME_CONFIG__ as
      | Record<string, unknown>
      | undefined;
    const runtimeBase = normalizeUrl(
      String(runtime?.API_BASE_URL || runtime?.apiBaseUrl || '')
    );
    if (runtimeBase) {
      return runtimeBase.endsWith('/api') ? runtimeBase : `${runtimeBase}/api`;
    }

    const host = String(window.location.hostname || '').toLowerCase();
    const protocol = window.location.protocol || 'https:';
    const port = String(window.location.port || '').trim();
    const portSuffix = port ? `:${port}` : '';
    const isLocal =
      host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
    if (isLocal) return `${protocol}//api.localhost${portSuffix}/api`;

    const parts = host.split('.').filter(Boolean);
    if (parts.length >= 2) {
      const base = parts.slice(-2).join('.');
      return `${protocol}//api.${base}${portSuffix}/api`;
    }
  }

  const envBase = normalizeUrl(
    process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL
  );
  if (envBase) return envBase.endsWith('/api') ? envBase : `${envBase}/api`;

  return '/api';
};

const resolveDiscoverReturnTo = (raw: string | null): string | undefined => {
  const value = String(raw || '').trim();
  if (!value) return undefined;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  if (/^https?:\/\//i.test(value)) return value;
  if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(value)) return `https://${value}`;
  return undefined;
};

const fetchWithTimeout = async (
  url: string,
  init: RequestInit,
  timeoutMs: number
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const extractErrorMessage = (payload: any, fallback: string): string =>
  String(
    payload?.error?.message ||
      payload?.message ||
      payload?.data?.message ||
      payload?.data?.error ||
      payload?.error ||
      fallback
  );

/* ──────────────────────────────── component ────────────────────────────────────── */

export default function LoginPage() {
  const { locale, setLocale, theme, setTheme } = useThemeAndLocale();
  const t = LOGIN_I18N[locale];
  const rtl = locale === 'ar';

  const location = useLocation();
  const returnTo = useMemo(
    () =>
      resolveDiscoverReturnTo(
        new URLSearchParams(location.search).get('returnTo')
      ),
    [location.search]
  );

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [keepSignedIn, setKeepSignedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoState, setLogoState] = useState<'intro' | 'converge' | ''>(
    'intro'
  );
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<DiscoverTenant[]>([]);

  useEffect(() => {
    const id = window.setTimeout(() => setLogoState(''), 1500);
    return () => window.clearTimeout(id);
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;
    setError(null);
    setTenants([]);
    setLoading(true);
    setLogoState('converge');

    try {
      const response = await fetchWithTimeout(
        `${getApiBaseUrl()}/auth/discover`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            email: email.trim(),
            password,
            keepSignedIn,
            ...(returnTo ? { returnTo } : {}),
          }),
        },
        REQUEST_TIMEOUT_MS
      );

      const payload = await response.json().catch(() => ({}));
      const data = payload?.data ?? payload;

      if (!response.ok || payload?.success === false) {
        setError(
          extractErrorMessage(payload, 'Authentication temporarily unavailable.')
        );
        return;
      }

      if (data?.redirectUrl) {
        window.location.replace(String(data.redirectUrl));
        return;
      }

      if (Array.isArray(data?.tenants) && data.tenants.length > 0) {
        setTenants(data.tenants as DiscoverTenant[]);
        return;
      }

      setError('Login succeeded but no destination was returned. Please try again.');
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setError('Authentication timed out after 30 seconds. Please try again.');
      } else {
        setError(err?.message || 'Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      className="min-h-screen w-full grid lg:[grid-template-columns:1.1fr_1fr] grid-cols-1 bg-app text-emphasis"
    >
      {/* ─────────────── ASIDE — editorial panel ─────────────── */}
      <aside
        className={`relative overflow-hidden flex flex-col gap-8 px-8 py-10 sm:px-12 sm:py-12 lg:py-12 lg:[grid-template-rows:auto_1fr_auto] lg:grid border-b lg:border-b-0 ${
          rtl ? 'lg:border-l' : 'lg:border-r'
        }`}
        style={{
          background: theme === 'dark' ? 'var(--ink-0)' : 'var(--paper-2)',
          color: theme === 'dark' ? 'var(--ink-8)' : 'var(--paper-7)',
          borderColor: theme === 'dark' ? 'var(--ink-3)' : 'var(--paper-3)',
        }}
      >
        {/* glow accent */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-32 w-[380px] h-[380px]"
          style={{
            background:
              'radial-gradient(circle, rgba(232,113,58,0.14), transparent 65%)',
          }}
        />

        {/* top — brand + version */}
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <BassitLogo
              size={36}
              intro={logoState === 'intro'}
              converge={logoState === 'converge'}
              pulse={loading}
            />
            <BassitWordmark variant={rtl ? 'ar' : 'en'} size={22} />
          </div>
          <div className="mono">v1.0 · Beta</div>
        </div>

        {/* middle — pull quote */}
        <div className="relative flex flex-col gap-[18px] max-w-[420px] lg:self-center">
          <div className="mono" style={{ color: 'var(--brand)' }}>
            {t.asideLabel}
          </div>
          <p
            className="display m-0"
            style={{
              fontSize: 'clamp(28px, 4vw, 36px)',
              lineHeight: 1.15,
              color: theme === 'dark' ? 'var(--ink-9)' : 'var(--paper-8)',
            }}
          >
            {t.pull1} <em>{t.pull2}</em>
          </p>
          <p
            className="m-0 text-[14px] leading-[1.6] max-w-[380px]"
            style={{ color: theme === 'dark' ? 'var(--ink-6)' : 'var(--paper-5)' }}
          >
            {t.pullAttr}
          </p>
        </div>

        {/* bottom — stats */}
        <div
          className="relative pt-6 border-t"
          style={{
            borderColor: theme === 'dark' ? 'var(--ink-3)' : 'var(--paper-3)',
          }}
        >
          <div className="grid grid-cols-3 gap-6">
            <Stat num="6" label={t.stat1} />
            <Stat num="4.2M" label={t.stat2} />
            <Stat num="EN/AR" label={t.stat3} />
          </div>
        </div>
      </aside>

      {/* ─────────────── FORM PANEL ─────────────── */}
      <section className="relative grid grid-rows-[auto_1fr_auto] px-6 sm:px-10 lg:px-12 py-8 lg:py-8 bg-app min-h-[100vh] lg:min-h-0">
        {/* head — breadcrumb + EN/AR + theme toggle */}
        <header className="flex items-center justify-between gap-3">
          <div className="mono text-muted">/ {rtl ? 'تسجيل' : 'login'}</div>
          <div className="flex items-center gap-3">
            <LangToggle locale={locale} onChange={setLocale} />
            <ThemeToggle theme={theme} onChange={setTheme} />
          </div>
        </header>

        {/* form */}
        <div className="self-center w-full max-w-[400px] mx-auto flex flex-col gap-6 py-10">
          <div className="mono" style={{ color: 'var(--brand)' }}>
            {t.eyebrowB}
          </div>
          <h1
            className="display m-0"
            style={{
              fontSize: 'clamp(44px, 8vw, 64px)',
              lineHeight: 1,
            }}
          >
            {t.titleB}
          </h1>
          <p
            className="m-0 text-[15px] leading-[1.5] max-w-[340px]"
            style={{ color: 'var(--secondary)' }}
          >
            {t.subB}
          </p>

          {error && (
            <div
              role="alert"
              className="rounded-md text-[13px] px-3 py-2.5"
              style={{
                background: 'rgba(232, 70, 58, 0.08)',
                border: '1px solid rgba(232, 70, 58, 0.4)',
                color: '#f4b6a8',
              }}
            >
              {error}
            </div>
          )}

          {tenants.length > 0 ? (
            <TenantList tenants={tenants} />
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-1">
              <Field
                label={t.workEmail}
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={setEmail}
                placeholder={t.emailPlaceB}
                disabled={loading}
                autoFocus
                required
              />
              <Field
                label={t.password}
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={setPassword}
                placeholder={t.pwPlaceB}
                disabled={loading}
                required
                trailing={
                  <a
                    className="mono transition-opacity hover:opacity-70"
                    style={{ color: 'var(--secondary)' }}
                    href="#"
                  >
                    {t.reset}
                  </a>
                }
              />

              <div className="flex items-center justify-between py-1">
                <label className="inline-flex items-center gap-[9px] text-[13px] cursor-pointer select-none" style={{ color: 'var(--secondary)' }}>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={keepSignedIn}
                    onChange={(e) => setKeepSignedIn(e.target.checked)}
                  />
                  <span
                    aria-hidden="true"
                    className="inline-block w-[14px] h-[14px] rounded-[3px] border relative transition-all"
                    style={{
                      borderColor: keepSignedIn ? 'var(--brand)' : 'var(--border)',
                      background: keepSignedIn ? 'var(--brand)' : 'transparent',
                    }}
                  >
                    {keepSignedIn && (
                      <span
                        aria-hidden="true"
                        className="absolute"
                        style={{
                          left: 3,
                          top: 0,
                          width: 4,
                          height: 8,
                          border: 'solid #1a0d05',
                          borderWidth: '0 1.5px 1.5px 0',
                          transform: 'rotate(45deg)',
                        }}
                      />
                    )}
                  </span>
                  <span>{t.keepSigned}</span>
                </label>
                <span className="mono text-muted">{t.session}</span>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-md text-[14px] font-medium tracking-[-0.005em] py-[13px] transition-all disabled:opacity-80"
                style={{
                  background: 'var(--brand)',
                  color: '#1a0d05',
                  border: '1px solid var(--brand)',
                }}
              >
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    {t.continue} <Arrow rtl={rtl} />
                  </>
                )}
              </button>
            </form>
          )}

          <div className="flex flex-col gap-[18px] mt-2">
            <div className="h-px border-soft border-t" />
            <div className="flex items-center justify-between text-[13px]">
              <span className="mono text-muted">{t.needAccess}</span>
              <a
                href="#"
                className="transition-opacity hover:opacity-75"
                style={{ color: 'var(--brand)' }}
              >
                {t.requestAdmin}
              </a>
            </div>
          </div>
        </div>

        {/* foot — copyright + cert */}
        <footer
          className="flex flex-wrap items-center justify-between gap-2 pt-4 border-t border-soft"
        >
          <span className="mono text-muted">© 2026 BassitAI · باسط</span>
          <span className="mono text-muted">{t.dataResidency}</span>
        </footer>
      </section>
    </div>
  );
}

/* ─────────────────────────────── small parts ─────────────────────────────────── */

function Stat({ num, label }: { num: string; label: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="display"
        style={{
          fontSize: 30,
          color: 'var(--brand)',
          letterSpacing: '-0.02em',
          fontWeight: 400,
        }}
      >
        {num}
      </div>
      <div className="mono" style={{ letterSpacing: '0.1em' }}>
        {label}
      </div>
    </div>
  );
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  autoComplete,
  required,
  disabled,
  autoFocus,
  trailing,
}: {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  trailing?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <label
          htmlFor={id}
          className="font-mono uppercase text-[11px] tracking-[0.12em]"
          style={{ color: 'var(--muted)' }}
        >
          {label}
        </label>
        {trailing}
      </div>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        required={required}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border outline-none transition-colors text-[15px] px-[14px] py-[11px] bg-elev focus:bg-raised"
        style={{
          borderColor: 'var(--border)',
          color: 'var(--text)',
          fontFamily: 'var(--font-sans)',
        }}
        onFocus={(e) =>
          (e.currentTarget.style.borderColor = 'var(--brand)')
        }
        onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
      />
    </div>
  );
}

function TenantList({ tenants }: { tenants: DiscoverTenant[] }) {
  return (
    <div className="grid gap-2.5">
      <h2 className="m-0 text-[18px]" style={{ color: 'var(--emphasis)' }}>
        Choose your organization
      </h2>
      {tenants.map((tenant) => (
        <button
          key={`${tenant.tenantId}-${tenant.redirectUrl}`}
          type="button"
          onClick={() => window.location.replace(tenant.redirectUrl)}
          className="w-full text-left rounded-md px-3 py-2.5 transition-colors hover:bg-raised border"
          style={{
            borderColor: 'var(--border)',
            background: 'var(--bg-elev)',
            color: 'var(--text)',
          }}
        >
          <strong>{tenant.tenantName || tenant.tenantHost || 'Tenant'}</strong>
          <div
            className="mt-1 text-[12px]"
            style={{ color: 'var(--secondary)' }}
          >
            {tenant.role || tenant.tenantHost || 'Continue'}
          </div>
        </button>
      ))}
    </div>
  );
}
